import {
    BackgroundImage,
    Button,
    Center,
    Group,
    Image,
    List,
    Mark,
    Paper,
    SimpleGrid,
    Stepper,
    Text,
    Title,
} from '@mantine/core';
import {useDisclosure} from '@mantine/hooks';
import {useState} from 'react';
import {ExpandingButton} from './ExpandingButton';
import styles from './GamePlayOverlay.module.css';

interface GameModalProps {
    opened: boolean;
    onClose: () => void;
}

function ObjectivesPanel() {
    return <>
        <Center><Title pt={'2%'}>Supply Chain Objectives</Title></Center>
        <SimpleGrid cols={2} m={'5%'}>
            <List spacing="lg" styles={{item: {fontSize: '1.4rem', textShadow: '0px 0px 0.5em #fff', fontWeight:'bold'}}}
                icon={<Image src={'images/rules-icon.svg'} w={'2em'} h={'2em'} />}
            >
                <List.Item>Each player has a personal objective, which is only visible to them.</List.Item>
                <List.Item>
                    To complete your personal objective, you must &quot;bank&quot; rings matching the colour of each
                    clip from left to right.
                </List.Item>
                <List.Item>The current objective you must bank is highlighted in pink.</List.Item>
                <List.Item fs={'italic'}>
                    The goal of the game is for <Mark color="blue">all players</Mark> to complete their personal objectives.
                </List.Item>
            </List>
            <Center>
                <Image src={'images/personal objective.png'} w={'18vw'} radius={'3em'} bd={'0.2em solid black'} bg={'white'}/>
            </Center>
        </SimpleGrid>
    </>;
}

export function RulesPanel() {
    return (
        <>
            <Center><Title>Supply Chain Rules</Title></Center>
            <List spacing="lg" m={'5%'} styles={{item: {fontSize: '1.4rem', textShadow: '0px 0px 0.5em #fff', fontWeight:'bold'}}}
                icon={<Image src={'images/rules-icon.svg'} w={'2em'} h={'2em'} />}
            >
                <List.Item>
                    Rings may only be passed along the connecting cords to the players immediately adjacent.
                </List.Item>
                <List.Item>You may only pass the bottom ring from your chain.</List.Item>
                <List.Item>
                    If you &quot;bank&quot; a ring on your personal objective card, it cannot be removed.
                </List.Item>
                <List.Item>
                    No one is allowed to be in possession of more than 4 rings at any one time. A ring
                    is in your possession until someone else receives it.
                </List.Item>
                <List.Item>
                    As soon as you have completed your personal objective card (and disposed of any remaining rings),
                    you will be frozen in the network. This closes down that line of communication.
                </List.Item>
            </List>
        </>
    );
}

function HowToPlayPanel() {
    return <>
        <Center><Title pt={'2%'}>How to play</Title></Center>
        <SimpleGrid cols={2} spacing={'xl'}>
            <Group mt={'md'}>
                <Text w={'40%'} size={'lg'} fw={'bold'}>Hover over an adjacent player to pass a ring to them.</Text>
                <Image src={'images/pass ring.png'} w={'13.5vw'} radius={'3em'} bd={'0.2em solid black'} bg={'white'}/>
            </Group>
            <Group mt={'md'}>
                <Text w={'40%'} size={'lg'} fw={'bold'}>Hover over yourself to bank a ring to your personal objective.</Text>
                <Image src={'images/bank ring.png'} w={'13.5vw'} radius={'3em'} bd={'0.2em solid black'} bg={'white'}/>
            </Group>
            <Group mt={'md'}>
                <Text w={'40%'} size={'lg'} fw={'bold'}>You can only pass a ring if the other player has capacity.</Text>
                <Image src={'images/no capacity.png'} w={'13.5vw'} radius={'3em'} bd={'0.2em solid black'} bg={'white'}/>
            </Group>
            <Group mt={'md'}>
                <Text w={'40%'} size={'lg'} fw={'bold'}>Banking is only available if the bottom ring on your chain matches the color of your next objective.</Text>
                <Image src={'images/cant bank.png'} w={'13.5vw'} radius={'3em'} bd={'0.2em solid black'} bg={'white'}/>
            </Group>
        </SimpleGrid>
    </>;
}

export function GameRulesPanel({opened, onClose}: GameModalProps) {
    const [active, setActive] = useState(0);
    const nextStep = () => setActive((current) => (current < 3 ? current + 1 : current));
    const prevStep = () => setActive((current) => (current > 0 ? current - 1 : current));
    const handleClose = () => {
        onClose();
        setActive(0);
    };

    if (!opened)
        return <></>;

    return (
        <Paper bg={'white'} pos={'relative'} w={'67%'} h={'80%'} left={'5%'} top={'5%'} style={{pointerEvents: 'initial'}}>
            <Image src="/images/greyBorderY.png" data-testid={'border_l'} pos={'absolute'} h={'100%'} w={'1%'} left={'0'}/>
            <Image src="/images/greyBorderY.png" data-testid={'border_r'} pos={'absolute'} h={'100%'} w={'1%'} right={'0'}/>
            <BackgroundImage src={'images/playerChatBackground.png'} w={'100%'} h={'100%'}>
                <BackgroundImage src={'images/rules-icon.svg'} w={'100%'} h={'100%'} pos={'absolute'} opacity={'0.1'}/>
                <Button onClick={handleClose} data-testid={'btn_close'} pos={'absolute'} top={'3%'} right={'3%'} bd={'0.15em solid white'} radius={'2em'} size={'md'}>
                    CLOSE
                </Button>
                <Stepper active={active} onStepClick={setActive} styles={{
                    root: {padding: '3%', height: '90%'},
                    steps: {padding: '0 15%'},
                    stepLabel: {fontWeight: 'bold', fontSize:'1.3em'},
                    content: {padding: '3%', height: '80%', pointerEvents: 'none'},
                }}>
                    <Stepper.Step label="Objective">
                        <ObjectivesPanel />
                    </Stepper.Step>
                    <Stepper.Step label="Rules">
                        <RulesPanel />
                    </Stepper.Step>
                    <Stepper.Step label="How to play">
                        <HowToPlayPanel />
                    </Stepper.Step>
                </Stepper>
                <Group justify="center">
                    <Button variant="default" onClick={prevStep} disabled={active === 0}>Back</Button>
                    {active < 2 &&
                        <Button onClick={nextStep} data-testid={'btn_next_step'}>Next step</Button>
                    }
                    {active === 2 &&
                        <Button onClick={handleClose}>Close</Button>
                    }
                </Group>
            </BackgroundImage>
            <Image src="/images/greyBorderX.png" data-testid={'border_t'} pos={'absolute'} h={'1.5%'} w={'100%'} top={'0'}/>
            <Image src="/images/greyBorderX.png" data-testid={'border_b'} pos={'absolute'} h={'1.5%'} w={'100%'} bottom={'0'}/>
        </Paper>
    );
}

export function GameRulesButton({defaultOpen = false}:{defaultOpen?: boolean}) {
    const [opened, {open, close}] = useDisclosure(defaultOpen);
    return (
        <>
            <GameRulesPanel opened={opened} onClose={close}/>
            <Group className={styles.ruleButtonContainer}>
                <ExpandingButton
                    active={false}
                    onClick={open}
                    icon="/images/rules-icon.svg"
                    label='RULES'
                    expandDirection='LEFT'
                />
            </Group>
        </>
    );
}
